import React, { useEffect, useState } from 'react';
import { MessageCircleMore, Plus, Save, Sparkles, Trash2 } from 'lucide-react';
import { engagement } from '../../../lib/api';
import { useLocale } from '../../../i18n/LocaleContext';

const DEFAULT_VOICE = {
  profile: 'Write directly, clearly, and with a useful point. Sound like a real person, not a brand account.',
  directness: 80,
  humor: 35,
  humanity: 70,
  length: 30,
  manual_examples: [],
};

const CONTROLS = [
  { key: 'directness', label: 'Directness', low: 'Diplomatic', high: 'Straight to the point' },
  { key: 'humor', label: 'Humor', low: 'Serious', high: 'Playful when appropriate' },
  { key: 'humanity', label: 'Human touch', low: 'Neutral', high: 'Natural and personal' },
  { key: 'length', label: 'Preferred length', low: 'Very short', high: 'More developed' },
];

export default function EngagementVoiceSettings() {
  const { t } = useLocale();
  const [voice, setVoice] = useState(DEFAULT_VOICE);
  const [example, setExample] = useState('');
  const [learnedCount, setLearnedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    engagement.getVoice()
      .then((data) => {
        if (!active) return;
        setVoice({ ...DEFAULT_VOICE, ...(data.settings || {}) });
        setLearnedCount(data.learned_examples_count || 0);
      })
      .catch((error) => active && setMessage({ type: 'error', text: error.message }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const addExample = () => {
    const text = example.trim();
    if (!text || voice.manual_examples.length >= 20) return;
    if (voice.manual_examples.some((item) => item.toLowerCase() === text.toLowerCase())) {
      setMessage({ type: 'error', text: t('This example is already added.') });
      return;
    }
    setVoice((current) => ({ ...current, manual_examples: [...current.manual_examples, text] }));
    setExample('');
    setMessage(null);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const data = await engagement.updateVoice({
        profile: voice.profile,
        directness: voice.directness,
        humor: voice.humor,
        humanity: voice.humanity,
        length: voice.length,
        manual_examples: voice.manual_examples,
      });
      setVoice({ ...DEFAULT_VOICE, ...(data.settings || {}) });
      setMessage({ type: 'success', text: t('Engagement Voice saved.') });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="engagement-voice" className="isolate w-full min-w-0 scroll-mt-5 rounded-xl border border-[#E3E2E0] bg-white p-4 sm:p-5" data-testid="engagement-voice-section">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FFF1E6]">
          <MessageCircleMore className="h-4 w-4 text-[#B54708]" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-[#191919]">{t('Engagement Voice')}</h2>
          <p className="mt-1 text-xs leading-5 text-[#6B6B6B]">
            {t('Define how public LinkedIn comments should sound. This is separate from Inbox AI Auto-Reply.')}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 h-40 animate-pulse rounded-xl bg-[#F5F5F3]" />
      ) : (
        <div className="mt-5 space-y-6">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-[#191919]">{t('Describe your voice')}</label>
            <textarea
              value={voice.profile}
              onChange={(event) => setVoice((current) => ({ ...current, profile: event.target.value }))}
              maxLength={1200}
              rows={4}
              className="w-full resize-y rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] px-3 py-2 text-sm leading-6 outline-none focus:border-[#12B8B5] focus:ring-2 focus:ring-[#12B8B5]/15"
              placeholder={t('Example: Direct, practical, occasionally dry humor. Avoid praise and corporate language.')}
              data-testid="engagement-voice-profile"
            />
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {CONTROLS.map((control) => (
              <label key={control.key} className="block rounded-xl border border-[#E8E8E5] bg-[#FBFBFA] p-4">
                <span className="flex items-center justify-between gap-3 text-xs font-semibold text-[#191919]">
                  {t(control.label)}
                  <span className="rounded-full bg-white px-2 py-1 tabular-nums text-[#667085]">{voice[control.key]}%</span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={voice[control.key]}
                  onChange={(event) => setVoice((current) => ({ ...current, [control.key]: Number(event.target.value) }))}
                  className="mt-3 w-full accent-[#12B8B5]"
                  data-testid={`engagement-voice-${control.key}`}
                />
                <span className="mt-1 flex justify-between gap-4 text-[11px] text-[#8A8A8A]">
                  <span>{t(control.low)}</span><span className="text-right">{t(control.high)}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="rounded-xl border border-[#BCEAE8] bg-[#F0FCFB] p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#0B5351]">
              <Sparkles className="h-4 w-4" /> {t('Voice preview')}
            </div>
            <p className="mt-2 text-sm leading-6 text-[#244746]">
              {t('The draft will lead with a useful point, use natural language, and add humor only when the post supports it.')}
            </p>
          </div>

          <div>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-[#191919]">{t('Manual style examples')}</h3>
                <p className="mt-1 text-xs text-[#6B6B6B]">
                  {t('Manual examples are prioritized. Successfully sent comments are learned automatically.')} {learnedCount} {t('sent examples learned')}.
                </p>
              </div>
              <span className="text-xs tabular-nums text-[#8A8A8A]">{voice.manual_examples.length}/20</span>
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <textarea
                value={example}
                onChange={(event) => setExample(event.target.value)}
                maxLength={800}
                rows={2}
                className="min-w-0 flex-1 resize-y rounded-lg border border-[#E3E2E0] px-3 py-2 text-sm outline-none focus:border-[#12B8B5]"
                placeholder={t('Paste a comment that sounds like you.')}
                data-testid="engagement-voice-example-input"
              />
              <button
                type="button"
                onClick={addExample}
                disabled={!example.trim() || voice.manual_examples.length >= 20}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-[#D7DCE4] bg-white px-4 py-2 text-sm font-medium text-[#0B1D3A] hover:bg-[#F7F8F6] disabled:opacity-40"
              >
                <Plus className="h-4 w-4" /> {t('Add example')}
              </button>
            </div>
            {voice.manual_examples.length > 0 && (
              <div className="mt-3 space-y-2">
                {voice.manual_examples.map((text, index) => (
                  <div key={`${index}-${text.slice(0, 20)}`} className="flex items-start gap-3 rounded-lg border border-[#E8E8E5] bg-white p-3">
                    <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm leading-5 text-[#343434]">{text}</p>
                    <button
                      type="button"
                      onClick={() => setVoice((current) => ({
                        ...current,
                        manual_examples: current.manual_examples.filter((_, itemIndex) => itemIndex !== index),
                      }))}
                      className="rounded-md p-1.5 text-[#B42318] hover:bg-[#FEF3F2]"
                      aria-label={t('Remove example')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {message && (
            <p className={`rounded-lg px-3 py-2 text-xs ${message.type === 'success' ? 'bg-[#E6F4EA] text-[#1E7F4E]' : 'bg-[#FEF3F2] text-[#B42318]'}`}>
              {message.text}
            </p>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving || !voice.profile.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0B1D3A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#142D52] disabled:opacity-50"
            data-testid="save-engagement-voice"
          >
            <Save className="h-4 w-4" /> {saving ? t('Saving...') : t('Save Engagement Voice')}
          </button>
        </div>
      )}
    </section>
  );
}
